import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/endpoints';
import PageHeader from '../../components/PageHeader';
import LiveTripMap from '../../components/LiveTripMap';
import {
  Badge, Button, Chip, Empty, Loading,
} from '../../components/Ui';
import { Reveal, stagger } from '../../components/Motion';
import { useToast } from '../../context/ToastContext';
import { onRealtime } from '../../services/realtime';
import { colors } from '../../theme';
import { currency, timeAgo } from '../../utils/format';

/** What a partner can move an order to next. Mirrors the server's flow. */
const NEXT = {
  placed: [
    { status: 'accepted', label: 'Accept', variant: 'success' },
    { status: 'rejected', label: 'Reject', variant: 'danger' },
  ],
  accepted: [
    { status: 'preparing', label: 'Start preparing', variant: 'primary' },
    { status: 'cancelled', label: 'Cancel', variant: 'danger' },
  ],
  preparing: [{ status: 'ready', label: 'Mark ready', variant: 'primary' }],
  // Once ready, buttons depend on whether a rider is involved - see nextFor.
  ready: [],
  out_for_delivery: [],
};

/** Riders in flight own the next step; a shop without one still hands off itself. */
const NO_RIDER = ['none', 'no_riders', undefined, null, ''];
const nextFor = (order) => {
  const base = NEXT[order.status] || [];
  const selfDelivering = NO_RIDER.includes(order.delivery && order.delivery.status);
  if (order.status === 'ready' && selfDelivering) {
    return [
      { status: 'out_for_delivery', label: 'Out for delivery', variant: 'primary' },
      { status: 'delivered', label: 'Delivered', variant: 'success' },
    ];
  }
  if (order.status === 'out_for_delivery' && selfDelivering) {
    return [{ status: 'delivered', label: 'Mark delivered', variant: 'success' }];
  }
  // Riders being rung, or one carrying it: nothing for the shop to press.
  return base;
};

/** Minutes since the order landed - the shop's clock against the target. */
const minutesSince = (from) => Math.max(0, Math.round((Date.now() - new Date(from)) / 60000));

const FILTERS = [
  { key: 'true', label: '🔥 Active' },
  { key: '', label: 'All' },
  { key: 'delivered', label: '✅ Delivered' },
  { key: 'cancelled', label: 'Cancelled' },
];

const PartnerOrdersPage = () => {
  const toast = useToast();
  const [filter, setFilter] = useState('true');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  /** `${orderId}:${status}` of the action in flight, so its button shows a spinner. */
  const [busy, setBusy] = useState(null);
  /** Ticks once a minute so the prep clock keeps counting while the tab sits open. */
  const [, setTick] = useState(0);

  const load = useCallback(() => {
    const params = filter === 'true' ? { active: 'true' } : filter ? { status: filter } : {};
    api.orders.partner({ ...params, limit: 40 })
      .then((res) => setItems(res.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  // New orders should appear without the partner refreshing the tab.
  useEffect(() => {
    const timer = setInterval(load, 20000);
    return () => clearInterval(timer);
  }, [load]);

  /*
   * A rider taking or collecting the parcel changes what this page may show,
   * so refresh the moment the server says something moved.
   */
  useEffect(() => onRealtime('order:delivery', () => load()), [load]);

  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 60000);
    return () => clearInterval(t);
  }, []);

  const setStatus = async (order, status, label) => {
    if (busy) return;                      // one action at a time, no double clicks
    if (['rejected', 'cancelled'].includes(status)) {
      const ok = await toast.confirm({
        title: `${label} order`,
        message: `${label} order ${order.orderNo}?`,
        confirmLabel: label,
        danger: true,
      });
      if (!ok) return;
    }
    setBusy(`${order._id}:${status}`);
    try {
      const res = await api.orders.setStatus(order._id, {
        status,
        note: ['rejected', 'cancelled'].includes(status) ? 'Declined by shop' : '',
      });
      // "Ready" kicks off rider dispatch - say what happened.
      if (status === 'ready' && res.message && res.message.includes('rider')) toast.success(res.message, 'Order ready');
      load();
    } catch (err) {
      toast.error(err.message, 'Could not update');
    } finally { setBusy(null); }
  };

  return (
    <div>
      <PageHeader
        title="Orders"
        back={false}
        subtitle={`${items.length} ${filter === 'true' ? 'active' : 'total'}`}
      />

      <div className="row no-bar" style={{ gap: 8, overflowX: 'auto', marginBottom: 16 }}>
        {FILTERS.map((f) => (
          <Chip key={f.key || 'all'} label={f.label} active={filter === f.key} onClick={() => setFilter(f.key)} />
        ))}
      </div>

      {loading ? <Loading /> : !items.length ? (
        <Empty emoji="🧾" title="No orders here" text="New customer orders will appear here instantly." />
      ) : (
        <div className="grid grid-list">
          {items.map((item, i) => (
            <Reveal key={item._id} delay={stagger(i, 45)} className="card sh-sm">
              <div className="row-between">
                <div className="grow">
                  <div className="t-title">{item.orderNo}</div>
                  <div className="t-tiny">
                    {timeAgo(item.createdAt)} · {item.items && item.items.length} items
                  </div>
                </div>
                <div className="flex" style={{ gap: 8, alignItems: 'center' }}>
                  {/* Raising a ticket from the order carries the number with it. */}
                  <Link
                    to={`/support?refType=order&refId=${item._id}&refNo=${encodeURIComponent(item.orderNo)}`}
                    className="btn ghost btn-sm"
                    title="Raise a support ticket about this order"
                  >
                    🎧
                  </Link>
                  <Badge value={item.status} />
                </div>
              </div>

              {/*
               * The prep clock. While the order is being made it counts up
               * against the target admin set and turns red once it is over, so
               * the shop sees it slipping rather than hearing it from a
               * customer. Once ready it freezes at what the shop actually took.
               */}
              {item.readyAt ? (
                <div
                  className="t-tiny"
                  style={{
                    display: 'inline-flex', gap: 6, alignItems: 'center', marginTop: 10,
                    background: colors.successBg, color: colors.success, fontWeight: 800,
                    borderRadius: 10, padding: '5px 10px',
                  }}
                >
                  ⏱️ Ready in {item.prepTookMinutes} min
                  {item.prepTargetMinutes ? ` · target ${item.prepTargetMinutes} min` : ''}
                </div>
              ) : ['placed', 'accepted', 'preparing'].includes(item.status) ? (() => {
                const mins = minutesSince(item.createdAt);
                const late = item.prepTargetMinutes && mins > item.prepTargetMinutes;
                return (
                  <div
                    className="t-tiny"
                    style={{
                      display: 'inline-flex', gap: 6, alignItems: 'center', marginTop: 10,
                      background: late ? colors.dangerBg : colors.warningBg,
                      color: late ? colors.danger : colors.warning, fontWeight: 800,
                      borderRadius: 10, padding: '5px 10px',
                    }}
                  >
                    ⏱️ {late ? `${mins - item.prepTargetMinutes} min over` : `${mins} min in`}
                    {item.prepTargetMinutes ? ` · mark ready within ${item.prepTargetMinutes} min` : ''}
                  </div>
                );
              })() : null}

              <div
                className="row"
                style={{
                  gap: 10, background: colors.bg, padding: 12,
                  borderRadius: 12, marginTop: 12, alignItems: 'flex-start',
                }}
              >
                <span style={{ fontSize: 15 }}>👤</span>
                <div className="grow">
                  <div className="t-body">{(item.address && item.address.name) || (item.user && item.user.name) || 'Customer'}{item.address && item.address.phone ? ` · ${item.address.phone}` : ''}
                    {item.address && item.address.forOther ? (
                      <div className="t-tiny" style={{ color: 'var(--accent)', fontWeight: 700 }}>👥 Booked by {(item.user && item.user.name) || 'a customer'}{item.user && item.user.phone ? ` (${item.user.phone})` : ''} for this person</div>
                    ) : null}</div>
                  <div className="t-tiny clamp-2">
                    📍 {[
                      item.address && item.address.line1,
                      item.address && item.address.landmark,
                      item.address && item.address.city,
                    ].filter(Boolean).join(', ')}
                  </div>
                </div>
                <a
                  href={`tel:${(item.address && item.address.phone) || (item.user && item.user.phone)}`}
                  className="press"
                  style={{
                    width: 34, height: 34, borderRadius: 10, background: '#fff',
                    display: 'grid', placeItems: 'center', flex: '0 0 auto',
                  }}
                  aria-label="Call customer"
                >
                  📞
                </a>
              </div>

              <div style={{ marginTop: 12 }}>
                {(item.items || []).slice(0, 3).map((line, li) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={li} className="row-between" style={{ padding: '4px 0' }}>
                    <span className="t-small clamp-1 grow">{line.title} × {line.quantity}</span>
                    <span className="t-small">{currency(line.total)}</span>
                  </div>
                ))}
                {item.items && item.items.length > 3 ? (
                  <div className="t-tiny">+{item.items.length - 3} more items</div>
                ) : null}
              </div>

              <div
                className="row wrap"
                style={{ gap: 10, marginTop: 12, paddingTop: 12, borderTop: `1px solid ${colors.line}` }}
              >
                <span className="t-title">Total {currency(item.totalAmount)}</span>
                <span className="grow" />
                <Badge
                  value={item.paymentMode === 'cod' ? 'Cash on delivery' : 'Paid online'}
                  color={item.paymentMode === 'cod' ? colors.warning : colors.success}
                />
              </div>

              {['assigned', 'at_shop'].includes(item.delivery && item.delivery.status) && item.delivery.ride && item.delivery.ride.pickup ? (
                <LiveTripMap ride={item.delivery.ride} who="shop" height={200} style={{ marginTop: 10 }} />
              ) : null}

              {/* Rider leg - what the shop sees while a rider carries the order. */}
              {item.delivery && item.delivery.status !== 'none' ? (
                <div className="row" style={{
                  gap: 10, marginTop: 10, padding: '8px 12px', borderRadius: 12,
                  background: colors.primaryLight, alignItems: 'center',
                }}>
                  <span style={{ fontSize: 15 }}>🛵</span>
                  <span className="t-small grow">
                    {{
                      searching: `Finding a rider - ${(item.delivery && item.delivery.ringNote) || 'nearby riders are being rung'}. If nobody answers we ring again, then widen the search.`,
                      assigned: `${(item.delivery.rider && item.delivery.rider.businessName) || 'Rider'} is coming to collect`,
                      at_shop: `${(item.delivery.rider && item.delivery.rider.businessName) || 'Rider'} is here - hand over the order`,
                      picked_up: `${(item.delivery.rider && item.delivery.rider.businessName) || 'Rider'} is delivering`,
                      delivered: `Delivered by ${(item.delivery.rider && item.delivery.rider.businessName) || 'rider'}`,
                      no_riders: 'No rider found nearby',
                    }[item.delivery.status]}
                  </span>
                  {item.delivery.status === 'no_riders' ? (
                    <Button title="Find rider" size="sm" onClick={async () => {
                      try { const r = await api.orders.findRider(item._id); toast.success(r.message, 'Finding rider'); load(); }
                      catch (err) { toast.error(err.message, 'Could not find rider'); }
                    }} />
                  ) : null}
                </div>
              ) : null}


              {nextFor(item)
                && !['assigned', 'at_shop', 'picked_up'].includes(item.delivery && item.delivery.status) ? (
                <div className="row" style={{ gap: 10, marginTop: 12 }}>
                  {nextFor(item).map((a) => (
                    <Button
                      key={a.status}
                      title={a.label}
                      variant={a.variant}
                      size="sm"
                      full
                      loading={busy === `${item._id}:${a.status}`}
                      disabled={Boolean(busy)}
                      onClick={() => setStatus(item, a.status, a.label)}
                    />
                  ))}
                </div>
              ) : null}
            </Reveal>
          ))}
        </div>
      )}

    </div>
  );
};

export default PartnerOrdersPage;
